import { z } from 'zod';

export const idSchema = z.string().uuid();
export type Id = z.infer<typeof idSchema>;

export const timestampsSchema = z.object({
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const tenantScopedSchema = z.object({
  tenantId: idSchema,
});

export const membershipRoles = ['tenant_admin', 'operator', 'viewer'] as const;
export const membershipRoleSchema = z.enum(membershipRoles);
export type MembershipRole = z.infer<typeof membershipRoleSchema>;

/** OrqForge platform-level admin role (from Keycloak 'orqforge' realm JWT). */
export const ORQFORGE_ADMIN_ROLE = 'orqforge_admin';

export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  search: z.string().trim().optional(),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export function paginated<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    total: z.number().int().nonnegative(),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
  });
}

export const tenantStatuses = ['active', 'suspended', 'deleted'] as const;
export const tenantStatusSchema = z.enum(tenantStatuses);
export type TenantStatus = z.infer<typeof tenantStatusSchema>;

export const tenantSchema = tenantScopedSchema
  .omit({ tenantId: true })
  .extend({
    id: idSchema,
    name: z.string().min(1).max(200),
    slug: z
      .string()
      .min(1)
      .max(80)
      .regex(/^[a-z0-9-]+$/, 'slug must be kebab-case'),
    isolationMode: z.enum(['shared', 'schema']).default('shared'),
    status: tenantStatusSchema.default('active'),
    keycloakRealm: z.string().max(120).optional(),
  })
  .merge(timestampsSchema);
export type Tenant = z.infer<typeof tenantSchema>;

export const tenantMembershipSchema = z.object({
  id: idSchema,
  tenantId: idSchema,
  userId: idSchema,
  role: membershipRoleSchema,
  createdAt: z.coerce.date(),
});
export type TenantMembership = z.infer<typeof tenantMembershipSchema>;

export const userSchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    email: z.string().email(),
    displayName: z.string().min(1).max(200),
    role: membershipRoleSchema,
    externalSubject: z.string().optional(),
  })
  .merge(timestampsSchema);
export type User = z.infer<typeof userSchema>;
